import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ListChecks, Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TaskInput {
  title: string;
  description?: string;
}

interface ImportActionTasksButtonProps {
  sessionId: string;
  workspaceId: string;
  tasks: TaskInput[];
}

export function ImportActionTasksButton({
  sessionId,
  workspaceId,
  tasks,
}: ImportActionTasksButtonProps) {
  const [isImporting, setIsImporting] = useState(false);
  const [isImported, setIsImported] = useState(false);
  const { toast } = useToast();

  const handleImport = async () => {
    if (tasks.length === 0) return;
    setIsImporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nicht eingeloggt");

      const { data: existing } = await supabase
        .from("action_tasks")
        .select("title")
        .eq("session_id", sessionId);

      const existingTitles = new Set(existing?.map(e => e.title) || []);
      const newTasks = tasks.filter(t => !existingTitles.has(t.title));

      if (newTasks.length === 0) {
        toast({ title: "Bereits importiert", description: "Alle Tasks wurden bereits hinzugefügt." });
        setIsImported(true);
        return;
      }

      const inserts = newTasks.map((task, i) => ({
        session_id: sessionId,
        workspace_id: workspaceId,
        title: task.title,
        description: task.description || null,
        source: "report",
        order_index: i,
        created_by: user.id,
      }));

      const { error } = await supabase.from("action_tasks").insert(inserts);
      if (error) throw error;

      toast({ title: "Tasks importiert", description: `${newTasks.length} Action Tasks erstellt.` });
      setIsImported(true);
    } catch (error: any) {
      toast({ title: "Fehler beim Import", description: error.message, variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  if (tasks.length === 0) return null;

  return (
    <Button
      variant={isImported ? "outline" : "secondary"}
      size="sm"
      onClick={handleImport}
      disabled={isImporting || isImported}
      className="gap-2"
    >
      {isImporting ? (
        <><Loader2 className="h-4 w-4 animate-spin" />Importiere...</>
      ) : isImported ? (
        <><Check className="h-4 w-4 text-green-600" />Tasks erstellt</>
      ) : (
        <><ListChecks className="h-4 w-4" />Action Tasks übernehmen</>
      )}
    </Button>
  );
}
